import { useEffect, useState } from 'react';
import type { Borrador, Sitio, Version, VersionCompleta, VistaPreviaWeb } from '../../api/ops/web';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Chip } from '../../componentes/Chip';
import { EstadoNoContratado } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Ventana } from '../../componentes/Ventana';
import { avisoBreveDeError } from '../../logica/estados';
import { diaYHora } from '../../logica/fechasCortas';
import { navegador } from '../../logica/navegador';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import { cambiosDesde, chipSitio, motivoRetirada, sinProtocolo } from './reglas';

/**
 * «Publicar» (13.3): lo cambiado desde la versión en la web (calculado aquí comparando el borrador con `getSiteVersion`,
 * revisión §5.9), el enlace privado de la vista previa (`createSitePreview`, enseña siempre el borrador actual, G577),
 * publicar con la revisión vista (`409 version_conflict` si otra persona lo cambió), las versiones y volver a una
 * (`restoreSiteVersion`, confirmando que se pierden los cambios sin publicar) y despublicar. Sin el bloque
 * `web_publica`, solo aquí se enseña el bloque «Web pública con su marca» (G611). Quién guardó o publicó sale de la lista
 * del equipo; si el rol no la puede leer, «Alguien del equipo» (revisión §5.8).
 */
interface Props extends PropsPantalla {
    abierto: boolean;
    onCerrar: () => void;
    sitio: Sitio;
    borrador: Borrador;
    nombreSeccion: (tipo: string) => string;
    onCambios: (n: number) => void;
    onPublicada: () => void;
}

const ALGUIEN = 'Alguien del equipo';

export function Publicar({ ops, negocio, movil, soloLectura, membresia, abierto, onCerrar, sitio, borrador, nombreSeccion, onCambios, onPublicada }: Props) {
    const { puede } = usePermisos();
    const avisar = useAvisoBreve();
    const [version, setVersion] = useState<VersionCompleta | null>(null);
    const [versiones, setVersiones] = useState<Version[] | null>(null);
    const [verVersiones, setVerVersiones] = useState(false);
    const [nombres, setNombres] = useState<Record<string, string>>({});
    const [conBloque, setConBloque] = useState<boolean | null>(null);
    const [vista, setVista] = useState<VistaPreviaWeb | null>(null);
    const [ocupado, setOcupado] = useState<string | null>(null);
    const [conflicto, setConflicto] = useState(false);
    const [volverA, setVolverA] = useState<Version | null>(null);
    const [despublicar, setDespublicar] = useState(false);
    const actual = sitio.current_version ?? null;
    const [antes, setAntes] = useState(abierto);
    if (antes !== abierto) {
        setAntes(abierto);
        if (abierto) {
            setConflicto(false);
        }
    }

    // La versión en la web, para contar los cambios (también para el botón de la cabecera).
    useEffect(() => {
        if (actual === null) {
            return;
        }
        let vivo = true;
        ops.getSiteVersion(negocio, actual.id)
            .then((v) => {
                if (vivo) {
                    setVersion(v);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, actual]);

    const cambios = cambiosDesde(borrador, actual === null ? null : version, nombreSeccion);
    useEffect(() => {
        onCambios(cambios.length);
    }, [cambios.length, onCambios]);

    useEffect(() => {
        if (!abierto) {
            return;
        }
        let vivo = true;
        ops.listSiteVersions(negocio, { limit: 20 })
            .then((l) => {
                if (vivo) {
                    setVersiones(l.data);
                }
            })
            .catch(() => undefined);
        ops.listEntitlements(negocio)
            .then((b) => {
                if (vivo) {
                    setConBloque(b.entitlements.some((e) => e.product_code === 'web_publica'));
                }
            })
            .catch(() => undefined);
        if (puede('equipo.miembros.ver')) {
            ops.listMembers(negocio, { limit: 100 })
                .then((l) => {
                    if (vivo) {
                        setNombres(Object.fromEntries(l.data.map((m) => [m.id, m.display_name])));
                    }
                })
                .catch(() => undefined);
        }
        return () => {
            vivo = false;
        };
    }, [abierto, ops, negocio, puede]);

    const quien = (id: string | null | undefined) => (id == null ? ALGUIEN : (nombres[id] ?? ALGUIEN));
    const retirada = sitio.active_takedown ?? null;
    const siguiente = (actual?.number ?? 0) + 1;
    const noSePublican = borrador.sections.filter((s) => s.visible && !s.publishable);
    const sinBloque = conBloque === false;
    const puedePublicar = !soloLectura && retirada === null && !sinBloque && (sitio.has_unpublished_changes || actual === null || sitio.status === 'withdrawn');

    const publicar = () => {
        setOcupado('publicar');
        ops.publishSite(negocio, vista?.draft_revision ?? borrador.revision)
            .then(() => {
                avisar({ tipo: 'ok', texto: `Publicada la versión ${String(siguiente)}.` });
                onPublicada();
                onCerrar();
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.codigo === 'version_conflict') {
                    setConflicto(true);
                    setVista(null);
                    onPublicada();
                } else {
                    avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido publicar.' });
                }
            })
            .finally(() => { setOcupado(null); });
    };

    const pedirVista = (abrir: boolean) => {
        setOcupado(abrir ? 'abrir' : 'copiar');
        ops.createSitePreview(negocio)
            .then((v) => {
                setVista(v);
                if (abrir) {
                    navegador.abrir(v.url);
                } else {
                    const portapapeles = navigator.clipboard as Clipboard | undefined;
                    void portapapeles?.writeText(v.url).then(() => { avisar({ tipo: 'ok', texto: 'Enlace copiado.' }); });
                }
            })
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido preparar la vista previa.' }); })
            .finally(() => { setOcupado(null); });
    };

    const chip = chipSitio(sitio);
    return (
        <>
            <PanelLateral
                abierto={abierto}
                onCerrar={onCerrar}
                movil={movil}
                titulo="Publicar"
                eyebrow="Tu web"
                subtitulo={sinProtocolo(sitio.public_url)}
                chip={chip.texto}
                chipEstado={chip.estado}
                icono="rocket"
                accion={puedePublicar ? `Publicar la versión ${String(siguiente)}` : ''}
                accionIcono="rocket"
                ocupado={ocupado === 'publicar'}
                ocupadoTexto="Publicando…"
                onAccion={publicar}
            >
                <div className="rv-wb-form">
                    {retirada !== null && (
                        <p className="rv-wb-banda rv-wb-banda--roja" role="alert">
                            <Icono nombre="ban" />
                            <span>
                                <strong>Web retirada por abuso.</strong> {`Motivo: ${motivoRetirada(retirada)}. Mientras siga retirada no se puede volver a publicar.`}
                            </span>
                        </p>
                    )}
                    {conflicto && (
                        <p className="rv-wb-banda" role="alert">
                            <Icono nombre="triangle-alert" />
                            <span>{`${quien(borrador.updated_by_member_id)} cambió el borrador (${diaYHora(borrador.updated_at)}) después de la vista previa. Vuelve a mirarla y publica después.`}</span>
                        </p>
                    )}
                    {actual !== null && (
                        <section aria-label="Cambios sin publicar">
                            <h3 className="rv-wb-subtitulo">
                                Cambios sin publicar <Chip estado="neutral">{String(cambios.length)}</Chip>
                            </h3>
                            <p className="rv-ca-nota rv-ca-nota--gris">{`Desde la versión ${String(actual.number)}. Se publican como la versión ${String(siguiente)}.`}</p>
                            {cambios.map((c) => (
                                <div key={`${c.parte}-${c.detalle}`} className="rv-wb-cambio">
                                    <span className="rv-ca-baldosa">
                                        <Icono nombre={c.icono} />
                                    </span>
                                    <span className="rv-ca-fila__texto">
                                        <strong>{c.parte}</strong>
                                        <span>{c.detalle}</span>
                                    </span>
                                </div>
                            ))}
                        </section>
                    )}
                    {noSePublican.map((s) => (
                        <p key={s.section_type} className="rv-wb-banda">
                            <Icono nombre="circle-alert" />
                            <span>{`«${nombreSeccion(s.section_type)}» no se publica: su módulo no está activo en el negocio. Se queda en el borrador.`}</span>
                        </p>
                    ))}
                    {sinBloque && (
                        <EstadoNoContratado
                            bloque="Web pública con su marca"
                            incluye={['Tu web en tu dirección de Reservaria', 'Con tu marca, tus secciones y tus idiomas']}
                            propietario={membresia?.is_owner === true}
                            onAnadir={null}
                            onInicio={null}
                            movil={movil}
                        />
                    )}
                    <section aria-label="Vista previa">
                        <h3 className="rv-wb-subtitulo">{sinProtocolo(sitio.public_url)}</h3>
                        <p className="rv-ca-nota rv-ca-nota--gris">
                            {[actual === null ? 'Sin publicar' : `Versión ${String(actual.number)} publicada ${diaYHora(actual.published_at)}`, `Borrador guardado ${diaYHora(borrador.updated_at)} por ${quien(borrador.updated_by_member_id)}`].join(' · ')}
                        </p>
                        <div className="rv-wb-botones">
                            <Boton tipo="secundario" icono="copy" ocupado={ocupado === 'copiar'} onClick={() => { pedirVista(false); }}>
                                Copiar el enlace
                            </Boton>
                            <Boton tipo="secundario" icono="external-link" ocupado={ocupado === 'abrir'} onClick={() => { pedirVista(true); }}>
                                Abrir la vista previa
                            </Boton>
                        </div>
                        <p className="rv-ca-nota rv-ca-nota--gris">
                            {`Enlace privado para enseñársela a alguien antes de publicar: enseña siempre el borrador actual.${vista === null ? ' Caduca en 24 horas.' : ` Caduca ${diaYHora(vista.expires_at)}.`}`}
                        </p>
                    </section>
                    {versiones !== null && versiones.length > 0 && (
                        <section className="rv-wb-versiones" aria-label="Versiones">
                            <button type="button" className="rv-wb-plegable" aria-expanded={verVersiones} onClick={() => { setVerVersiones((v) => !v); }}>
                                <span className="rv-ca-baldosa">
                                    <Icono nombre="history" />
                                </span>
                                <span className="rv-ca-fila__texto">
                                    <strong>Versiones</strong>
                                    <span>{`${String(versiones.length)} ${versiones.length === 1 ? 'versión' : 'versiones'}${actual === null ? '' : ` · tus clientes ven la ${String(actual.number)}`}`}</span>
                                </span>
                                <Icono nombre={verVersiones ? 'chevron-up' : 'chevron-down'} />
                            </button>
                            {verVersiones &&
                                versiones.map((v) => (
                                    <div key={v.id} className="rv-wb-cambio">
                                        <span className="rv-ca-fila__texto">
                                            <strong>
                                                {`Versión ${String(v.number)}`} {v.id === actual?.id && <Chip estado="confirmed">En la web</Chip>}
                                            </strong>
                                            <span>{`${diaYHora(v.published_at)} · ${quien(v.published_by_member_id)}${v.source === 'restore' ? ' · vuelta a una anterior' : ''}`}</span>
                                        </span>
                                        {v.id !== actual?.id && !soloLectura && retirada === null && (
                                            <Boton tipo="secundario" icono="undo-2" onClick={() => { setVolverA(v); }}>
                                                Volver a esta
                                            </Boton>
                                        )}
                                    </div>
                                ))}
                        </section>
                    )}
                    {actual !== null && sitio.status !== 'withdrawn' && (
                        <div>
                            <Boton tipo="secundario" rojo icono="eye-off" onClick={() => { setDespublicar(true); }}>
                                Despublicar la web
                            </Boton>
                        </div>
                    )}
                </div>
            </PanelLateral>
            <Ventana
                abierta={volverA !== null}
                titulo={volverA === null ? '' : `Volver a la versión ${String(volverA.number)}`}
                texto={sitio.has_unpublished_changes ? 'Se publica otra versión igual a esa y se pierden los cambios del borrador que no habías publicado.' : 'Se publica otra versión igual a esa.'}
                cancelar="Cancelar"
                accion={sitio.has_unpublished_changes ? 'Perder los cambios y volver' : 'Volver a esta versión'}
                ocupado={ocupado === 'volver'}
                movil={movil}
                onCerrar={() => { setVolverA(null); }}
                onAccion={() => {
                    if (volverA === null) {
                        return;
                    }
                    setOcupado('volver');
                    ops.restoreSiteVersion(negocio, volverA.id, sitio.has_unpublished_changes)
                        .then((v) => {
                            avisar({ tipo: 'ok', texto: `Publicada la versión ${String(v.number)}.` });
                            setVolverA(null);
                            onPublicada();
                            onCerrar();
                        })
                        .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido volver a esa versión.' }); })
                        .finally(() => { setOcupado(null); });
                }}
            />
            <Ventana
                abierta={despublicar}
                titulo="Despublicar la web"
                texto="Tus clientes dejarán de verla hasta que la vuelvas a publicar. El borrador y las versiones se quedan."
                cancelar="Cancelar"
                accion="Despublicar"
                ocupado={ocupado === 'despublicar'}
                movil={movil}
                onCerrar={() => { setDespublicar(false); }}
                onAccion={() => {
                    setOcupado('despublicar');
                    ops.unpublishSite(negocio)
                        .then(() => {
                            avisar({ tipo: 'ok', texto: 'Web despublicada.' });
                            setDespublicar(false);
                            onPublicada();
                            onCerrar();
                        })
                        .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido despublicar.' }); })
                        .finally(() => { setOcupado(null); });
                }}
            />
        </>
    );
}
